import type { FC, ReactNode } from 'react'
import type { PreviewFlag } from '@/lib/cloud/preview'
import type { Project } from '@/types'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { shapeKey } from '@/lib/format'
import { PointCloud } from '@/components/generals/point-cloud'

interface ProjectHeroProps {
    project: Project
    number: string
    total: number
}

/**
 * Full-width point cloud of the project with its two flags, the figure label and the title.
 */
export const ProjectHero: FC<ProjectHeroProps> = ({ project, number, total }): ReactNode => {
    const [view, setView] = useState({ yaw: 36, pitch: 28 })
    const inProgress = project.status.trim().toLowerCase() === 'in progress'

    const flags = useMemo(() => {
        const list: Array<PreviewFlag> = []

        if (project.flagOneCode || project.flagOneText) {
            list.push({
                id: project.flagOneCode,
                text: project.flagOneText,
                anchor: 'flag1',
                leader: 72,
                cms: `projects:${project.id}:flag_one_text`,
                cmsId: `projects:${project.id}:flag_one_code`
            })
        }

        if (project.flagTwoCode || project.flagTwoText) {
            list.push({
                id: project.flagTwoCode,
                text: project.flagTwoText,
                anchor: 'flag2',
                leader: 104,
                dots: inProgress,
                cms: `projects:${project.id}:flag_two_text`,
                cmsId: `projects:${project.id}:flag_two_code`
            })
        }

        return list
    }, [project, inProgress])

    const flagSummary = [project.flagOneCode && `${project.flagOneCode}, ${project.flagOneText}`, project.flagTwoCode && `${project.flagTwoCode}, ${project.flagTwoText}`]
        .filter(Boolean)
        .join('; ')

    return (
        <section aria-labelledby='project-title' className='relative h-[78svh] min-h-[520px] overflow-hidden border-b border-hairline desk:h-[calc(100svh-4rem)] desk:min-h-[620px]'>
            <PointCloud
                key={project.slug}
                shape={shapeKey(project.shape, project.slug)}
                label={`Point cloud of ${project.name} in ${project.city}${inProgress ? ', shown as a partial scan while construction continues' : ''}. ${flagSummary ? `Flags: ${flagSummary}.` : ''} Drag to orbit the model.`}
                budget='hero'
                rotate='always'
                rotateSpeed={2.6}
                hoverBoost={2.4}
                interactive
                effects
                frame
                grid
                viewShift={0.12}
                flags={flags}
                grow={inProgress}
                onFrame={info => {
                    const yaw = Math.round(info.yaw) % 360
                    const pitch = Math.round(info.pitch)

                    setView(current => (current.yaw === yaw && current.pitch === pitch ? current : { yaw, pitch }))
                }}
                className='absolute inset-0 cursor-grab touch-pan-y active:cursor-grabbing'
            />

            <div className='pointer-events-none absolute inset-0 z-[3]'>
                <div className='absolute left-gutter right-gutter top-5 desk:right-auto desk:top-8' data-ruler-mark='01'>
                    <p className='hud rise-fade text-ink'>
                        Fig. 01 / Project {number} of {String(total).padStart(2, '0')} / {inProgress ? 'Partial scan' : 'Point cloud'}
                    </p>

                    <p className='hud rise-fade text-mute'>
                        Local grid / 1 : 400
                    </p>
                </div>

                <div className='absolute right-gutter top-20 hidden text-right sm:block desk:top-8' aria-hidden='true'>
                    <p className='hud text-ink'>
                        Yaw {String(view.yaw).padStart(3, '0')} / Pitch {String(view.pitch).padStart(2, '0')}
                    </p>

                    <p className='hud text-mute'>
                        Drag to orbit
                    </p>
                </div>

                <div className='absolute bottom-8 left-gutter right-gutter desk:bottom-12 desk:right-auto'>
                    <Link href='/projects' className='hud pointer-events-auto inline-flex items-center gap-2 text-mute transition-colors duration-300 hover:text-ink'>
                        <ArrowLeft className='size-4' strokeWidth={1.5} aria-hidden='true' />
                        All projects
                    </Link>

                    <h1 id='project-title' className='display-xl mt-6'>
                        <span className='rise-line'>
                            <span className='rise-inner text-ink' data-mdlfy-cms={`projects:${project.id}:name`}>
                                {project.name}
                            </span>
                        </span>

                        <span className='rise-line'>
                            <span className='rise-inner text-dim' style={{ animationDelay: '90ms' }}>
                                <span data-mdlfy-cms={`projects:${project.id}:city`}>
                                    {project.city}
                                </span>
                                , {project.year}
                            </span>
                        </span>
                    </h1>
                </div>
            </div>
        </section>
    )
}
